// Даты и время в интерфейсе: дд.мм.гггг и чч:мм по часовому поясу установки (README «Тексты»;
// spec, «Граничные случаи»). Хранится время в UTC, показывается — в поясе установки.

export const DEFAULT_TIME_ZONE = 'Asia/Yekaterinburg';

interface DateParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('ru-RU', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

function partsOf(date: Date, timeZone: string): DateParts {
  const result: DateParts = { year: '', month: '', day: '', hour: '', minute: '' };
  for (const part of formatterFor(timeZone).formatToParts(date)) {
    if (part.type in result) result[part.type as keyof DateParts] = part.value;
  }
  return result;
}

/** «14.10.2026 15:42». */
export function formatDateTime(iso: string, timeZone: string): string {
  const p = partsOf(new Date(iso), timeZone);
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
}

/** «15:42». */
export function formatTime(iso: string, timeZone: string): string {
  const p = partsOf(new Date(iso), timeZone);
  return `${p.hour}:${p.minute}`;
}

/** День момента времени: «14.10.2026». */
export function formatDayOf(iso: string, timeZone: string): string {
  const p = partsOf(new Date(iso), timeZone);
  return `${p.day}.${p.month}.${p.year}`;
}

/** Дата без времени «2026-10-31» → «31.10.2026». */
export function formatDate(date: string): string {
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}

/** Время, если момент сегодня, иначе дата и время: «до 15:40» или «до 15.10.2026 09:12». */
export function formatMoment(iso: string, timeZone: string, now: Date = new Date()): string {
  return dateIn(timeZone, new Date(iso)) === dateIn(timeZone, now)
    ? formatTime(iso, timeZone)
    : formatDateTime(iso, timeZone);
}

/** День момента в поясе установки: «2026-10-14». */
export function dateIn(timeZone: string, date: Date): string {
  const p = partsOf(date, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Сегодня в поясе установки: «2026-10-14». */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return dateIn(timeZone, now);
}

/** Сдвиг даты на N дней: календарная арифметика без пояса. */
export function shiftDate(date: string, days: number): string {
  const moment = new Date(`${date}T12:00:00Z`);
  moment.setUTCDate(moment.getUTCDate() + days);
  return moment.toISOString().slice(0, 10);
}

/** Смещение пояса на дату: «+05:00». */
function offsetOn(date: string, timeZone: string): string {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
    .formatToParts(new Date(`${date}T12:00:00Z`))
    .find((part) => part.type === 'timeZoneName')?.value;
  const match = /GMT([+-]\d{2}:\d{2})/.exec(name ?? '');
  return match?.[1] ?? '+00:00';
}

/** Начало дня в поясе установки — для фильтра периода: «2026-10-14T00:00:00+05:00». */
export function dayStart(date: string, timeZone: string): string {
  return `${date}T00:00:00${offsetOn(date, timeZone)}`;
}

/** Конец дня в поясе установки, включительно. */
export function dayEnd(date: string, timeZone: string): string {
  return `${date}T23:59:59.999${offsetOn(date, timeZone)}`;
}

const zoneCities: Readonly<Record<string, string>> = {
  'Asia/Yekaterinburg': 'Екатеринбург',
  'Europe/Moscow': 'Москва',
  'Europe/Kaliningrad': 'Калининград',
  'Europe/Samara': 'Самара',
  'Asia/Omsk': 'Омск',
  UTC: 'UTC',
};

/** Город пояса для заголовка колонки «Время, Екатеринбург»; неизвестный пояс — как есть. */
export function zoneLabel(timeZone: string): string {
  return zoneCities[timeZone] ?? timeZone;
}
